/**
 * ids.ts — id + slug helpers for the library.
 *
 * Library entries are keyed by a stable **uuid** (immutable, survives rename)
 * and carry a separate editable **slug** (the human id, unique within the
 * library). This module is environment-safe: `crypto.randomUUID()` is used
 * where available (localhost dev over https/http + any https deploy), with a
 * Math.random fallback for insecure contexts.
 */

/** Generate an RFC-4122 v4 uuid, falling back to a 128-bit random hex string
 *  when `crypto.randomUUID` isn't available (e.g. a plain http origin that
 *  isn't localhost — not our dev case, but be robust). */
export function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // non-secure context — fall through to manual generation
  }
  // 16 random bytes → hex. Not RFC-conformant but globally unique for our use.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Turn a free-form label into a filesystem/URL-safe slug.
 *  Lowercase, words joined by `-`, accents stripped, non-alphanumerics dropped. */
export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip combining marks
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

/** Resolve a base slug against taken slugs so it stays unique.
 *  Appends `-2`, `-3`, … until a free slot is found. Returns `base` itself
 *  when untouched. Empty input yields a `scenario-<uuid-suffix>` default. */
export function uniqueSlug(taken: Iterable<string>, base?: string): string {
  const takenSet = new Set(taken);
  let root = base ? slugify(base) : '';
  if (!root) root = `scenario-${uuid().slice(0, 8)}`;
  if (!takenSet.has(root)) return root;
  let n = 2;
  while (takenSet.has(`${root}-${n}`)) n++;
  return `${root}-${n}`;
}
